import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { useToast } from '../context/ToastContext';
import CountdownTimer from '../components/ui/CountdownTimer';
import AnnouncementBanner from '../components/ui/AnnouncementBanner';
import AlertMessage from '../components/ui/AlertMessage';
import { WarningIcon } from '../components/ui/Icons';
import CryptoPaymentModal from '../components/ui/CryptoPaymentModal';
import PanelTabs from '../components/ui/PanelTabs';
import PasswordInput from '../components/ui/PasswordInput';
import PasswordRules from '../components/ui/PasswordRules';
import { roleBadgeClass } from '../constants/roles';
import FarmingTab from './user/FarmingTab';
import PanelCard, { EmptyState } from '../components/ui/PanelCard';
import CopyButton from '../components/ui/CopyButton';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const TAB_ICONS = {
  overview:  <><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>,
  orders:    <><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><path d="M3 6h18M16 10a4 4 0 01-8 0" /></>,
  history:   <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  activate:  <><path d="M15 7a5 5 0 11-4.9 6H7v3H4v-3l-1-1 1-1h6.1A5 5 0 0115 7z" /></>,
  invites:   <><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></>,
  referral:  <><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" /></>,
  security:  <><path d="M12 3l8 3v6c0 4.4-3.4 8.3-8 9-4.6-.7-8-4.6-8-9V6l8-3z" /><path d="M9 12l2 2 4-4" /></>,
  farming:   <><path d="M3 3v18h18" /><path d="M7 15l4-5 3 3 5-7" /></>,
};

function SubStatus({ sub }) {
  if (!sub) return (
    <span className="badge-inactive"><span className="w-1.5 h-1.5 rounded-full bg-gray-600" />No active subscription</span>
  );
  if (sub.lifetime) return (
    <span className="badge-brand"><span className="w-1.5 h-1.5 rounded-full bg-brand-300" />Lifetime</span>
  );
  const expired = new Date(sub.expires_at) < new Date();
  if (expired) return (
    <span className="badge-inactive"><span className="w-1.5 h-1.5 rounded-full bg-gray-600" />Subscription expired</span>
  );
  if (sub.is_frozen) return (
    <span className="badge-frozen"><span className="w-1.5 h-1.5 rounded-full bg-sky-400" />Frozen</span>
  );
  return (
    <span className="badge-active"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Active</span>
  );
}

function InviteStatusBadge({ status }) {
  const map = {
    pending:  { cls: 'bg-amber-500/12 text-amber-300 border-amber-500/25',     label: 'Pending' },
    approved: { cls: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/25', label: 'Approved' },
    rejected: { cls: 'bg-red-500/12 text-red-300 border-red-500/25',           label: 'Rejected' },
  };
  const { cls, label } = map[status] || map.pending;
  return (
    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border flex-shrink-0 ${cls}`}>
      {label}
    </span>
  );
}

export default function UserPanel() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [siteStatus, setSiteStatus] = useState('working');

  const [dlLoading, setDlLoading] = useState(false);

  const [keyValue, setKeyValue] = useState('');
  const [keyMsg, setKeyMsg] = useState(null);
  const [keyLoading, setKeyLoading] = useState(false);

  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);
  const [referral, setReferral] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [emailForm, setEmailForm] = useState({ password: '', newEmail: '' });
  const [emailMsg, setEmailMsg] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);

  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [invites, setInvites] = useState([]);
  const [inviteReason, setInviteReason] = useState('');
  const [inviteMsg, setInviteMsg] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSystemOn, setInviteSystemOn] = useState(false);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [resumeOrder, setResumeOrder] = useState(null);

  const [discordStatus, setDiscordStatus] = useState(null);
  const [discordLoading, setDiscordLoading] = useState(false);

  const loadProfile = () => api.get('/user/profile').then(res => setProfile(res.data));
  const loadDiscordStatus = () => api.get('/discord/status').then(res => setDiscordStatus(res.data)).catch(() => {});
  const loadInvites = () => api.get('/invite/my').then(res => setInvites(res.data));
  const loadOrders = () => {
    setOrdersLoading(true);
    return api.get('/user/orders').then(res => setOrders(res.data.orders || [])).catch(() => {}).finally(() => setOrdersLoading(false));
  };

  useEffect(() => {
    // Discord OAuth visszatérés kezelése (?discord=...)
    const params = new URLSearchParams(window.location.search);
    const d = params.get('discord');
    if (d) {
      const map = {
        linked:          ['success', 'Discord account connected!'],
        taken:           ['error',   'This Discord account is already linked to another user.'],
        not_in_guild:    ['error',   'Join our Discord server first, then connect again.'],
        bot_unreachable: ['error',   'Could not reach the Discord bot. Please try again later.'],
        error:           ['error',   'Discord linking failed. Please try again.'],
      };
      const [kind, text] = map[d] || map.error;
      toast[kind](text);
      window.history.replaceState({}, '', '/panel');
    }

    Promise.all([
      loadProfile(),
      api.get('/owner/status').then(res => setSiteStatus(res.data.status)),
      api.get('/invite/status').then(res => setInviteSystemOn(res.data.inviteSystem === 'on')),
      loadInvites(),
      loadOrders(),
      loadDiscordStatus(),
    ]).finally(() => setLoading(false));

    const interval = setInterval(() => {
      api.get('/user/profile').then(res => setProfile(res.data)).catch(() => {});
      api.get('/owner/status').then(res => setSiteStatus(res.data.status)).catch(() => {});
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadLoader = async () => {
    setDlLoading(true);
    try {
      const res = await api.get('/user/download-loader', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mortis_engine.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed. Please try again.');
    } finally {
      setDlLoading(false);
    }
  };

  const linkDiscord = async () => {
    setDiscordLoading(true);
    try {
      const res = await api.get('/discord/start');
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not start Discord linking.');
      setDiscordLoading(false);
    }
  };

  const unlinkDiscord = async () => {
    setDiscordLoading(true);
    try {
      await api.post('/discord/unlink');
      toast.success('Discord account disconnected.');
      await loadDiscordStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || 'An error occurred.');
    } finally {
      setDiscordLoading(false);
    }
  };

  const activateKey = async (e) => {
    e.preventDefault();
    setKeyMsg(null);
    setKeyLoading(true);
    try {
      const res = await api.post('/user/activate-key', { keyValue });
      setKeyMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
      setKeyValue('');
      await loadProfile();
    } catch (err) {
      setKeyMsg({ type: 'error', text: err.response?.data?.error || 'An error occurred.' });
    } finally {
      setKeyLoading(false);
    }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    setDeleteMsg(null);
    setDeleteLoading(true);
    try {
      await api.delete('/user/account', { data: { password: deletePassword } });
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/';
    } catch (err) {
      setDeleteMsg({ type: 'error', text: err.response?.data?.error || 'An error occurred.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) {
      setPwMsg({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    setPwMsg(null);
    setPwLoading(true);
    try {
      const res = await api.post('/user/change-password', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      setPwMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.error || 'An error occurred.' });
    } finally {
      setPwLoading(false);
    }
  };

  const loadReferral = useCallback(async () => {
    try {
      const { data } = await api.get('/referral/me');
      setReferral(data);
    } catch {
      /* a fül enélkül is működik, csak üres */
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'referral') loadReferral();
  }, [activeTab, loadReferral]);

  const copyReferral = () => {
    navigator.clipboard?.writeText(referral.code).then(() => {
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 1500);
    }).catch(() => {});
  };

  // Az email csak akkor íródik át, ha az ÚJ címre küldött linket megnyitják.
  const changeEmail = async (e) => {
    e.preventDefault();
    setEmailMsg(null);
    setEmailLoading(true);
    try {
      const res = await api.post('/user/change-email', {
        password: emailForm.password,
        newEmail: emailForm.newEmail,
      });
      setEmailMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
      setPendingEmail(res.data.pendingEmail);
      setEmailForm({ password: '', newEmail: '' });
    } catch (err) {
      setEmailMsg({ type: 'error', text: err.response?.data?.error || 'An error occurred.' });
    } finally {
      setEmailLoading(false);
    }
  };

  const submitInviteRequest = async (e) => {
    e.preventDefault();
    setInviteMsg(null);
    setInviteLoading(true);
    try {
      const res = await api.post('/invite/request', { reason: inviteReason });
      setInviteMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
      setInviteReason('');
      await loadInvites();
    } catch (err) {
      setInviteMsg({ type: 'error', text: err.response?.data?.error || 'An error occurred.' });
    } finally {
      setInviteLoading(false);
    }
  };

  if (loading) return <LoadingSpinner fullPage label="Loading your dashboard…" />;

  const sub = profile?.subscription;
  const history = profile?.history || [];
  const hwid = profile?.user?.hwid;
  const isUpdating = siteStatus === 'updating';
  const hasActiveSub = sub && (sub.lifetime || (new Date(sub.expires_at) > new Date() && !sub.is_frozen));
  const hasPendingRequest = invites.some(i => i.status === 'pending');
  const unusedInvites = invites.filter(i => i.status === 'approved' && i.code && !i.used_at).length;

  const tabs = [
    { id: 'overview',  label: 'Overview',     icon: TAB_ICONS.overview },
    // A rendelések és az előfizetés-előzmény ugyanannak a kérdésnek a két fele
    // ("mi történt eddig?"), ezért egy lenyíló mögé kerülnek.
    {
      id: 'activity',
      label: 'Orders',
      icon: TAB_ICONS.orders,
      items: [
        { id: 'orders',  label: 'Purchase orders',     hint: 'Crypto and card payments' },
        { id: 'history', label: 'Subscription history', hint: 'Past, expired subscriptions' },
      ],
    },
    { id: 'farming',   label: 'Farming',      icon: TAB_ICONS.farming },
    { id: 'activate',  label: 'Activate Key', icon: TAB_ICONS.activate },
    { id: 'invites',   label: 'Invites',      icon: TAB_ICONS.invites, count: unusedInvites },
    { id: 'referral',  label: 'Referral',     icon: TAB_ICONS.referral },
    { id: 'security',  label: 'Security',     icon: TAB_ICONS.security },
  ];

  // max-w-5xl (nem 4xl): így a fülsor nagyobb kijelzőn egy sorba fér ki
  return (
    <div className="page-container max-w-5xl">
      {resumeOrder && (
        <CryptoPaymentModal
          order={resumeOrder}
          onClose={() => setResumeOrder(null)}
          onExpire={() => {
            toast.error('Payment expired.');
            setResumeOrder(null);
            loadOrders();
          }}
          onSuccess={() => {
            toast.success('Payment confirmed! Your key is ready.');
            loadOrders();
          }}
        />
      )}
      <AnnouncementBanner />

      <header className="flex items-center gap-4 mb-8">
        <div className="relative flex-shrink-0">
          <span className="absolute -inset-1.5 rounded-2xl bg-brand-500/20 blur-lg" />
          <div className="relative w-14 h-14 rounded-2xl bg-brand-gradient border border-brand-400/25
                          flex items-center justify-center text-white text-xl font-bold font-display
                          shadow-glow-sm">
            {user?.username?.[0]?.toUpperCase()}
          </div>
        </div>
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold text-white tracking-tight truncate">
            {user?.username}
          </h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded border
                              ${roleBadgeClass(user?.role)}`}>{user?.role}</span>
            <SubStatus sub={sub} />
          </div>
        </div>
      </header>

      <PanelTabs tabs={tabs} active={activeTab} onChange={setActiveTab} className="mb-6" />

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <PanelCard title="Account Details" className="min-w-0">
              <div className="surface-sunken px-4">
                <InfoRow label="Username" value={user?.username} />
                <InfoRow label="Email" value={profile?.user?.email} />
                <InfoRow label="Role" value={user?.role} />
                <InfoRow label="HWID" value={hwid || '—'} mono copyable />
                <InfoRow label="Registered" value={profile?.user?.created_at?.slice(0, 10)} />
              </div>
            </PanelCard>

            <PanelCard title="Subscription" className="min-w-0">
              {sub ? (
                <>
                  <div className="surface-sunken p-4 mb-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-3">
                      Time remaining
                    </p>
                    <CountdownTimer expiresAt={sub.expires_at} frozen={sub.is_frozen} lifetime={sub.lifetime} />
                  </div>
                  <div className="surface-sunken px-4">
                    <InfoRow label="Product" value={sub.product_name} />
                    <InfoRow label="Plan" value={sub.plan_label} />
                    <InfoRow label="HWID" value={sub.hwid || '—'} mono copyable />
                    <InfoRow label="Expires" value={sub.lifetime ? 'Never' : sub.expires_at?.slice(0, 16).replace('T', ' ')} />
                    <InfoRow label="Status" value={sub.lifetime ? 'Lifetime' : sub.is_frozen ? 'Frozen' : new Date(sub.expires_at) < new Date() ? 'Expired' : 'Active'} />
                  </div>
                </>
              ) : (
                <EmptyState
                  icon={<><rect x="4" y="11" width="16" height="9" rx="2" /><path d="M8 11V7a4 4 0 118 0v4" /></>}
                  title="No active subscription"
                  hint="Pick a plan on the home page to get started."
                  className="py-8"
                />
              )}
            </PanelCard>
          </div>

          <DiscordCard
            status={discordStatus}
            loading={discordLoading}
            onLink={linkDiscord}
            onUnlink={unlinkDiscord}
          />

          {hasActiveSub && (
            <div className="card card-interactive p-6 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3.5 min-w-0">
                <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-brand-500/12 border border-brand-400/20
                                 text-brand-200 flex items-center justify-center">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <path d="M7 10l5 5 5-5M12 15V3" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <h2 className="font-display font-semibold text-white">Loader Download</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Download the latest version of the MortisEngine loader.
                  </p>
                </div>
              </div>
              <button onClick={downloadLoader} disabled={dlLoading} className="btn-primary flex-shrink-0">
                {dlLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Downloading…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <path d="M7 10l5 5 5-5M12 15V3" />
                    </svg>
                    Download Loader
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Orders */}
      {activeTab === 'orders' && (
        <PanelCard flush title="Purchase Orders" subtitle="Your crypto and card payment history.">
          {ordersLoading ? (
            <div className="py-12 flex justify-center"><LoadingSpinner /></div>
          ) : orders.length === 0 ? (
            <EmptyState
              icon={<><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><path d="M3 6h18M16 10a4 4 0 01-8 0" /></>}
              title="No orders yet."
              hint="Purchases you make will show up here."
            />
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {orders.map(order => {
                const isPendingCrypto = order.status === 'pending' && order.payment_method === 'crypto';
                const isExpired = order.status === 'pending' && new Date(order.expires_at) < new Date();
                const effectiveStatus = isExpired ? 'expired' : order.status;

                const dot = {
                  delivered: 'bg-emerald-400',
                  confirmed: 'bg-brand-400 animate-pulse',
                  pending:   'bg-amber-400 animate-pulse',
                }[effectiveStatus] || 'bg-gray-700';

                const statusTone = {
                  delivered: 'text-emerald-400',
                  confirmed: 'text-brand-300',
                  pending:   'text-amber-400',
                }[effectiveStatus] || 'text-gray-600';

                const statusLabel = {
                  delivered: 'Delivered',
                  confirmed: 'Confirmed',
                  pending:   'Pending',
                }[effectiveStatus] || 'Expired';

                return (
                  <div key={order.id} className="px-5 py-4 flex items-center justify-between gap-4
                                                 flex-wrap hover:bg-white/[0.02] transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">{order.plan_label}</p>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {order.payment_method === 'crypto' ? 'Litecoin' : 'Card'}
                          <span className="mx-1.5 text-gray-700">·</span>
                          {order.created_at?.slice(0, 10)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                      {isPendingCrypto && !isExpired && (
                        <button
                          onClick={() => setResumeOrder({
                            ...order,
                            ltcAddress: order.ltc_address,
                            ltcAmount: order.ltc_amount,
                            plan: { label: order.plan_label },
                          })}
                          className="btn-outline text-xs px-3 py-1.5"
                        >
                          Resume
                        </button>
                      )}

                      {effectiveStatus === 'delivered' && order.license_key && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-lg
                                        bg-emerald-500/[0.08] border border-emerald-500/20">
                          <code className="font-mono text-xs text-emerald-300">{order.license_key}</code>
                          <CopyButton value={order.license_key} variant="icon" />
                        </div>
                      )}

                      <div className="text-right">
                        <p className="text-sm font-semibold text-white tabular-nums">€{order.final_price}</p>
                        <span className={`text-xs font-medium ${statusTone}`}>{statusLabel}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </PanelCard>
      )}

      {/* History */}
      {activeTab === 'history' && (
        <PanelCard flush title="Subscription History" subtitle="Your past, expired subscriptions.">
          {history.length === 0 ? (
            <EmptyState
              icon={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>}
              title="No past subscriptions yet."
            />
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {history.map(h => (
                <div key={h.id} className="px-5 py-4 flex items-center justify-between gap-4
                                           hover:bg-white/[0.02] transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{h.product_name}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{h.plan_label}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400">Activated: {h.created_at?.slice(0, 10)}</p>
                    <p className="text-xs text-gray-600 mt-0.5">Expired: {h.expires_at?.slice(0, 10)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </PanelCard>
      )}

      {/* Activate Key */}
      {activeTab === 'farming' && <FarmingTab />}

      {activeTab === 'activate' && (
        <PanelCard
          title="Activate Key"
          subtitle="Enter the key you received after purchase."
          className="max-w-md"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                 strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <path d="M15 7a5 5 0 11-4.9 6H7v3H4v-3l-1-1 1-1h6.1A5 5 0 0115 7z" />
            </svg>
          }
        >
          {isUpdating && (
            <div className="px-4 py-3 bg-red-500/10 border border-red-500/25 rounded-lg
                            text-red-300 text-sm mb-5 flex items-start gap-2.5">
              <WarningIcon />
              Key activation is disabled while the service is being updated.
            </div>
          )}

          <AlertMessage msg={keyMsg} className="mb-5" />

          <form onSubmit={activateKey} className="space-y-4">
            <div>
              <label className="label">License Key</label>
              <input type="text" className={`input font-mono tracking-wider uppercase text-center ${isUpdating ? 'opacity-45 cursor-not-allowed' : ''}`}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                value={keyValue}
                onChange={e => !isUpdating && setKeyValue(e.target.value.toUpperCase())}
                readOnly={isUpdating} required />
            </div>
            <div>
              <label className="label">HWID</label>
              <div className="surface-sunken px-4 py-2.5 font-mono text-sm text-gray-500 select-all break-all">
                {hwid || 'Not yet assigned'}
              </div>
              <p className="text-xs text-gray-600 mt-2">Assigned automatically by the desktop app.</p>
            </div>
            <button type="submit" className="btn-primary w-full py-2.5"
              disabled={keyLoading || isUpdating}>
              {keyLoading ? 'Activating…' : isUpdating ? 'Activation Disabled' : 'Activate Key'}
            </button>
          </form>
        </PanelCard>
      )}

      {/* Invites */}
      {activeTab === 'invites' && (
        <div className="space-y-6">
          {inviteSystemOn ? (
            <PanelCard
              title="Request Invite Code"
              subtitle="Only users with an active subscription can request an invite code. Your request will be reviewed by an admin."
              className="max-w-md"
            >
              {!hasActiveSub && (
                <div className="px-4 py-3 bg-amber-500/10 border border-amber-500/25 rounded-lg
                                text-amber-300 text-sm mb-4 flex items-start gap-2.5">
                  <WarningIcon />
                  You need an active subscription to request an invite code.
                </div>
              )}

              {hasPendingRequest && (
                <div className="px-4 py-3 bg-sky-500/10 border border-sky-500/25 rounded-lg
                                text-sky-300 text-sm mb-4">
                  You already have a pending request. Please wait for admin review.
                </div>
              )}

              <AlertMessage msg={inviteMsg} className="mb-4" />

              <form onSubmit={submitInviteRequest} className="space-y-4">
                <div>
                  <label className="label">Reason for request</label>
                  <textarea
                    className="textarea min-h-[6rem]"
                    placeholder="Explain why you want to invite someone…"
                    value={inviteReason}
                    onChange={e => setInviteReason(e.target.value)}
                    disabled={!hasActiveSub || hasPendingRequest}
                    required
                  />
                  <p className={`text-xs mt-2 ${
                    inviteReason.length > 500 ? 'text-red-400' : 'text-gray-600'
                  }`}>
                    {inviteReason.length}/500 characters (min. 10)
                  </p>
                </div>
                <button type="submit" className="btn-primary w-full py-2.5"
                  disabled={inviteLoading || !hasActiveSub || hasPendingRequest}>
                  {inviteLoading ? 'Submitting…' : 'Submit Request'}
                </button>
              </form>
            </PanelCard>
          ) : (
            <div className="card max-w-md">
              <EmptyState
                icon={<><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" /></>}
                title="The invite system is currently disabled."
              />
            </div>
          )}

          {invites.length > 0 && (
            <PanelCard flush title="My Invite History">
              <div className="divide-y divide-white/[0.05]">
                {invites.map(inv => (
                  <div key={inv.id} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-300 leading-relaxed">{inv.reason}</p>
                        <p className="text-xs text-gray-600 mt-1.5 font-mono">
                          {inv.created_at?.slice(0, 16).replace('T', ' ')}
                        </p>
                      </div>
                      <InviteStatusBadge status={inv.status} />
                    </div>

                    {inv.status === 'approved' && inv.code && (
                      <div className="mt-3 surface-sunken p-3.5">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1">
                              Invite Code
                            </p>
                            <div className="flex items-center gap-1">
                              <code className="font-mono text-brand-200 text-sm tracking-widest">{inv.code}</code>
                              <CopyButton value={inv.code} variant="icon" />
                            </div>
                          </div>
                          <div className="text-right">
                            {inv.used_at ? (
                              <div>
                                <span className="badge-inactive">Used</span>
                                <p className="text-xs text-gray-600 mt-1.5">by {inv.used_by_name}</p>
                                <p className="text-xs text-gray-700">{inv.used_at?.slice(0, 10)}</p>
                              </div>
                            ) : (
                              <span className="badge-active">Available</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {inv.status === 'rejected' && (
                      <p className="text-xs text-gray-600 mt-1.5">
                        Reviewed by {inv.reviewed_by_name} on {inv.reviewed_at?.slice(0, 10)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </PanelCard>
          )}
        </div>
      )}

      {/* Referral */}
      {activeTab === 'referral' && (
        <div className="space-y-6 max-w-2xl">
          {referral && !referral.enabled && (
            <div className="bg-amber-500/10 border border-amber-500/25 text-amber-300 text-sm px-4 py-3 rounded-lg">
              The referral system is currently turned off, so codes can't be redeemed right now.
            </div>
          )}

          <PanelCard
            title="Your referral code"
            subtitle={
              <>
                Share this code. When someone uses it on their first purchase, you get
                <strong className="text-gray-300"> +{referral?.rewardDays ?? 7} days</strong> added
                to your subscription. It only counts on plans longer than
                {' '}{referral?.rewardDays ?? 7} days, it only works while your own
                subscription is active, and each account can use a code once.
              </>
            }
          >
            <div className="flex gap-2 mb-6">
              <div className="flex-1 px-4 py-3 rounded-lg bg-black/30 border border-brand-400/20
                              font-mono tracking-[0.35em] text-center text-lg text-white select-all
                              shadow-glow-sm">
                {referral?.code || '········'}
              </div>
              <button
                onClick={copyReferral}
                disabled={!referral?.code}
                className="btn-secondary px-5 whitespace-nowrap"
              >
                {referralCopied ? 'Copied' : 'Copy'}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="surface-sunken p-4 text-center">
                <div className="font-display text-2xl font-bold text-white tabular-nums">
                  {referral?.totalReferrals ?? 0}
                </div>
                <div className="text-[11px] text-gray-600 mt-1.5 uppercase tracking-wider">
                  People referred
                </div>
              </div>
              <div className="surface-sunken p-4 text-center">
                <div className="font-display text-2xl font-bold text-brand-200 tabular-nums">
                  +{referral?.totalDaysEarned ?? 0}
                </div>
                <div className="text-[11px] text-gray-600 mt-1.5 uppercase tracking-wider">
                  Days earned
                </div>
              </div>
            </div>
          </PanelCard>

          {referral?.redemptions?.length > 0 && (
            <PanelCard title="Referral history">
              <div className="surface-sunken px-4">
                {referral.redemptions.map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-3
                                          border-b border-white/[0.05] last:border-0">
                    <span className="text-sm text-gray-300">{r.referred_username}</span>
                    <div className="text-right">
                      <span className="text-sm text-brand-200 font-medium">+{r.days_granted} days</span>
                      <div className="text-xs text-gray-600 mt-0.5">{r.created_at?.slice(0, 10)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </PanelCard>
          )}

          {referral?.usedCodeFrom && (
            <p className="text-xs text-gray-600">
              You used {referral.usedCodeFrom.referrer_username}'s code on this account.
            </p>
          )}
        </div>
      )}

      {/* Security */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          {/* Jelszó és email csere egymás mellett — nagy képernyőn elfér */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <PanelCard
              title="Change Password"
              subtitle="You will be logged out after changing your password."
            >
              <AlertMessage msg={pwMsg} className="mb-5" />

              <form onSubmit={changePassword} className="space-y-4">
                <div>
                  <label className="label">Current Password</label>
                  <PasswordInput
                    value={pwForm.currentPassword}
                    onChange={v => setPwForm(p => ({ ...p, currentPassword: v }))}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <div>
                  <label className="label">New Password</label>
                  <PasswordInput
                    value={pwForm.newPassword}
                    onChange={v => setPwForm(p => ({ ...p, newPassword: v }))}
                    autoComplete="new-password"
                    required
                  />
                  <PasswordRules value={pwForm.newPassword} />
                </div>
                <div>
                  <label className="label">Confirm New Password</label>
                  <PasswordInput
                    value={pwForm.confirm}
                    onChange={v => setPwForm(p => ({ ...p, confirm: v }))}
                    autoComplete="new-password"
                    required
                  />
                </div>
                <button type="submit" className="btn-primary w-full py-2.5" disabled={pwLoading}>
                  {pwLoading ? 'Saving…' : 'Change Password'}
                </button>
              </form>
            </PanelCard>

            <PanelCard
              title="Change Email Address"
              subtitle={
                <>
                  Current address: <span className="text-gray-300">{profile?.user?.email}</span>.
                  The change only takes effect once you open the confirmation link we send to the new address.
                </>
              }
            >
              <AlertMessage msg={emailMsg} className="mb-5" />

              {pendingEmail && (
                <div className="bg-brand-500/10 border border-brand-400/25 text-brand-200 text-sm
                                px-4 py-3 rounded-lg mb-5 leading-relaxed">
                  Waiting for confirmation on <strong className="text-white">{pendingEmail}</strong>.
                  The link is valid for 24 hours — until then your account keeps using {profile?.user?.email}.
                </div>
              )}

              <form onSubmit={changeEmail} className="space-y-4">
                <div>
                  <label className="label">New Email</label>
                  <input type="email" className="input" placeholder="email@example.com"
                    value={emailForm.newEmail}
                    onChange={e => setEmailForm(p => ({ ...p, newEmail: e.target.value }))}
                    autoComplete="email" required />
                </div>
                <div>
                  <label className="label">Confirm with your password</label>
                  <PasswordInput
                    value={emailForm.password}
                    onChange={v => setEmailForm(p => ({ ...p, password: v }))}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <button type="submit" className="btn-primary w-full py-2.5" disabled={emailLoading}>
                  {emailLoading ? 'Sending link…' : 'Send confirmation link'}
                </button>
              </form>
            </PanelCard>

          </div>

          {/* Veszélyzóna — külön vizuális nyelv, hogy ne lehessen véletlenül idetévedni */}
          <div className="card p-6 max-w-md border-red-500/20">
            <div className="flex items-start gap-3 mb-5">
              <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-red-500/12 border border-red-500/25
                               text-red-300 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                     strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" />
                  <path d="M19 6l-.9 13.1A2 2 0 0116.1 21H7.9a2 2 0 01-2-1.9L5 6" />
                </svg>
              </span>
              <div>
                <h2 className="font-display text-base font-semibold text-red-300">Delete Account</h2>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                  Permanently anonymizes your account data in accordance with our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="link underline">
                    Privacy Policy
                  </a>.
                  Your orders and financial records are retained for legal compliance.
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <AlertMessage msg={deleteMsg} className="mb-4" />

            {!deleteConfirm ? (
              <button onClick={() => setDeleteConfirm(true)} className="btn-danger w-full py-2.5">
                Request Account Deletion
              </button>
            ) : (
              <form onSubmit={deleteAccount} className="space-y-4">
                <div className="px-4 py-3 bg-red-500/10 border border-red-500/25 rounded-lg
                                text-red-300 text-xs leading-relaxed">
                  Your username, email, HWID and session data will be permanently wiped.
                  Active subscriptions will be cancelled. This cannot be reversed.
                </div>
                <div>
                  <label className="label">Confirm with your password</label>
                  <PasswordInput
                    value={deletePassword}
                    onChange={setDeletePassword}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setDeleteConfirm(false); setDeletePassword(''); setDeleteMsg(null); }}
                    className="btn-secondary flex-1 py-2.5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={deleteLoading || !deletePassword}
                    className="btn-danger flex-1 py-2.5"
                  >
                    {deleteLoading ? 'Deleting…' : 'Delete My Account'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ ok, okText, badText }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1
                      rounded-full border ${
      ok
        ? 'bg-emerald-500/12 text-emerald-300 border-emerald-500/25'
        : 'bg-white/[0.05] text-gray-500 border-white/[0.08]'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-emerald-400' : 'bg-gray-600'}`} />
      {ok ? okText : badText}
    </span>
  );
}

function DiscordCard({ status, loading, onLink, onUnlink }) {
  const linked = status?.linked;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-[#5865F2]/15 border border-[#5865F2]/25
                          flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-[#5865F2]" fill="currentColor" aria-hidden="true">
              <path d="M20.317 4.37a19.79 19.79 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.865-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.74 19.74 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 00.031.056 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.1 13.1 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 01.079.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.3 12.3 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.84 19.84 0 006.002-3.03.077.077 0 00.032-.055c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 00-.031-.029zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
            </svg>
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-semibold text-white">Discord</h2>
            {linked ? (
              <p className="text-sm text-gray-500 truncate mt-0.5">
                Connected as <span className="text-gray-200 font-medium">{status.discordUsername}</span>
              </p>
            ) : (
              <p className="text-sm text-gray-500 mt-0.5">Link your Discord to receive the customer role.</p>
            )}
          </div>
        </div>

        {linked ? (
          <button onClick={onUnlink} disabled={loading} className="btn-secondary text-sm py-2 flex-shrink-0">
            {loading ? 'Working…' : 'Disconnect'}
          </button>
        ) : (
          <button
            onClick={onLink}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg flex-shrink-0
                       text-white font-medium bg-[#5865F2] hover:bg-[#4752c4]
                       border border-white/10 transition-all duration-200 ease-smooth
                       hover:-translate-y-px disabled:opacity-45 disabled:cursor-not-allowed
                       disabled:hover:translate-y-0"
            style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 20px -10px rgba(88,101,242,0.8)' }}
          >
            {loading ? 'Redirecting…' : 'Connect Discord'}
          </button>
        )}
      </div>

      {linked && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/[0.06]">
          <StatusPill ok={status.inGuild} okText="In server" badText="Not in server" />
          <StatusPill ok={status.hasCustomerRole} okText="Customer role" badText="No customer role" />
          {!status.subActive && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1
                             rounded-full border bg-amber-500/12 text-amber-300 border-amber-500/25">
              No active subscription
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, mono, copyable }) {
  return (
    <div className="flex justify-between items-center text-sm py-2.5 gap-4
                    border-b border-white/[0.05] last:border-0">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className="flex items-center gap-1 min-w-0">
        <span
          className={`text-gray-200 min-w-0 truncate text-right ${mono ? 'font-mono text-xs' : ''}`}
          title={mono ? value : undefined}
        >{value}</span>
        {copyable && value && value !== '—' && <CopyButton value={value} variant="icon" />}
      </span>
    </div>
  );
}
